/**
 * Standardized Validation Messages
 * 
 * This file provides consistent, descriptive validation error messages
 * across the entire application following the pattern:
 * "Field Name is required" or "Field Name must be [description]"
 */

export const validationMessages = {
  // Projects
  project: {
    name: {
      required: 'Project Name is required',
      minLength: 'Project Name must be at least 3 characters',
      maxLength: 'Project Name must not exceed 100 characters'
    },
    key: {
      required: 'Project Key is required',
      format: 'Project Key must be at least 2 characters, start with a letter, and contain only uppercase letters and numbers',
      duplicate: 'Project Key already exists'
    },
    workflow: {
      required: 'Workflow is required',
      invalid: 'Please select a valid workflow'
    },
    description: {
      empty: 'Project Description cannot be empty',
      maxLength: 'Project Description must not exceed 2000 characters'
    }
  },

  // Tasks / Issues
  task: {
    title: {
      required: 'Task Title is required',
      minLength: 'Task Title must be at least 3 characters',
      maxLength: 'Task Title must not exceed 255 characters'
    },
    type: {
      required: 'Task Type is required',
      invalid: 'Please select a valid task type'
    },
    description: {
      maxLength: 'Task Description must not exceed 5000 characters'
    },
    assignee: {
      invalid: 'Please select a valid assignee'
    },
    sprint: {
      invalid: 'Please select a valid sprint'
    },
    priority: {
      invalid: 'Please select a valid priority'
    },
    status: {
      invalid: 'Please select a valid status'
    },
    comment: {
      required: 'Comment cannot be empty',
      minLength: 'Comment must be at least 1 character',
      maxLength: 'Comment must not exceed 5000 characters'
    },
    subtask: {
      parentRequired: 'Subtasks must have a parent task. Change the type first or delete the subtask.',
      invalidParent: 'Subtasks can only be under Stories or Tasks, not Epics.'
    }
  },

  // Users & Accounts
  user: {
    email: {
      required: 'Email Address is required',
      format: 'Invalid email format',
      duplicate: 'Email Address already in use'
    },
    firstName: {
      required: 'First Name is required',
      minLength: 'First Name must be at least 2 characters',
      maxLength: 'First Name must not exceed 50 characters'
    },
    lastName: {
      required: 'Last Name is required',
      minLength: 'Last Name must be at least 2 characters',
      maxLength: 'Last Name must not exceed 50 characters'
    },
    password: {
      required: 'Password is required',
      minLength: 'Password must be at least 8 characters',
      uppercase: 'Password must contain at least one uppercase letter',
      lowercase: 'Password must contain at least one lowercase letter',
      digit: 'Password must contain at least one digit',
      special: 'Password must contain at least one special character'
    },
    confirmPassword: {
      required: 'Please confirm your new password',
      mismatch: 'Passwords do not match'
    },
    currentPassword: {
      required: 'Current password is required'
    },
    newPassword: {
      required: 'New password is required'
    }
  },

  // Workflows
  workflow: {
    name: {
      required: 'Workflow Name is required',
      minLength: 'Workflow Name must be at least 3 characters',
      maxLength: 'Workflow Name must not exceed 100 characters'
    },
    status: {
      required: 'At least one status is required',
      initialRequired: 'Workflow must have at least one initial status',
      terminalRequired: 'Workflow must have at least one terminal (completed) status',
      key: {
        required: 'Status Key is required',
        format: 'Status Key must be a valid identifier'
      },
      name: {
        required: 'Status Name is required'
      }
    }
  },

  // Task Types
  taskType: {
    name: {
      required: 'Task Type Name is required',
      minLength: 'Task Type Name must be at least 2 characters',
      maxLength: 'Task Type Name must not exceed 50 characters'
    },
    key: {
      required: 'Task Type Key is required',
      format: 'Task Type Key must be a valid identifier',
      duplicate: 'Task Type Key already exists'
    },
    icon: {
      required: 'Please select a file',
      format: 'Icon must be a valid image file (PNG, JPG, WEBP)',
      size: 'Icon file size must not exceed 2MB'
    },
    system: {
      cannotDelete: 'System default types cannot be deleted'
    }
  },

  // Sprints
  sprint: {
    name: {
      required: 'Sprint Name is required',
      minLength: 'Sprint Name must be at least 2 characters',
      maxLength: 'Sprint Name must not exceed 100 characters'
    },
    startDate: {
      required: 'Start Date is required',
      invalid: 'Invalid start date'
    },
    endDate: {
      required: 'End Date is required',
      invalid: 'Invalid end date',
      beforeStart: 'End Date must be after Start Date'
    },
    goal: {
      maxLength: 'Sprint Goal must not exceed 1000 characters'
    }
  },

  // Authentication
  auth: {
    email: {
      required: 'Email Address is required',
      format: 'Please enter a valid email address'
    },
    password: {
      required: 'Password is required'
    },
    otp: {
      required: 'Please enter the complete 6-digit code',
      format: 'Invalid OTP code',
      expired: 'OTP code has expired'
    },
    notFound: 'Email not found. Please go back and enter your email again.',
    invalidLink: 'Invalid invitation link. No token provided.'
  },

  // Files & Attachments
  file: {
    required: 'Please select a file',
    format: 'Invalid file type. Please upload PNG, JPG, or WEBP image.',
    size: 'File size must not exceed 10MB',
    download: 'Failed to download file'
  },

  // General / Common
  general: {
    userSelection: 'Please select a user',
    fieldRequired: (fieldName) => `${fieldName} is required`,
    mustSelect: (fieldName) => `Please select a ${fieldName}`,
    cannotBeEmpty: (fieldName) => `${fieldName} cannot be empty`,
    invalidFormat: (fieldName) => `Invalid ${fieldName} format`,
    characterLimit: (fieldName, limit) => `${fieldName} must not exceed ${limit} characters`,
    minCharacters: (fieldName, min) => `${fieldName} must be at least ${min} characters`
  }
};

/**
 * Helper function to get validation message for a field
 * @param {string} section - Section like 'project', 'task', 'user'
 * @param {string} field - Field name
 * @param {string} type - Error type like 'required', 'format', etc.
 * @returns {string} - The validation message
 */
export const getValidationMessage = (section, field, type) => {
  try {
    return validationMessages[section]?.[field]?.[type] || 'Invalid input';
  } catch (error) {
    return 'Invalid input';
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password complexity
 * @param {string} password - Password to validate
 * @returns {object} - { isValid, missing } where missing is an array of missing requirements
 */
export const validatePassword = (password) => {
  const requirements = [
    { name: 'uppercase', regex: /[A-Z]/, message: 'uppercase letter' },
    { name: 'lowercase', regex: /[a-z]/, message: 'lowercase letter' },
    { name: 'digit', regex: /\d/, message: 'digit' }
  ];

  const missing = [];
  requirements.forEach(req => {
    if (!req.regex.test(password)) {
      missing.push(req.message);
    }
  });

  return {
    isValid: password.length >= 8 && missing.length === 0,
    missing
  };
};

/**
 * Validate project key format (uppercase letters and numbers, starting with letter)
 * @param {string} key - Key to validate
 * @returns {boolean} - True if valid
 */
export const isValidProjectKey = (key) => {
  return /^[A-Z][A-Z0-9]*$/.test(key) && key.length >= 2;
};
